/* Theme cart limits: serialize requests and check a fresh cart before writing.
   Show quantity guidance when shoppers attempt an unavailable addition. */
(() => {
  'use strict';
  if (window.LoungeMouetteLimits) return;

  /* 上限値・文言・判定方法はすべて Liquid 側（テーマ設定と翻訳ファイル）から受け取る。
     このファイルには数字も日本語も持たせない。 */
  const defaults = {maxPerProduct: 1, maxTotal: 20, matchByTitle: true, messages: {}};
  const readConfig = () => {
    const node = document.querySelector('[data-mouette-config]');
    if (!node) return {...defaults};
    try { return {...defaults, ...JSON.parse(node.textContent)}; }
    catch { return {...defaults}; }
  };
  const config = readConfig();
  const maxPerProduct = Number(config.maxPerProduct) || 0;
  const maxTotal = Number(config.maxTotal) || 0;
  // 上限がどちらも 0 なら、この機能そのものを動かさない。
  if (maxPerProduct <= 0 && maxTotal <= 0) return;
  const perProductLimit = maxPerProduct > 0 ? maxPerProduct : Infinity;
  const totalLimit = maxTotal > 0 ? maxTotal : Infinity;

  const sampleName = name => config.matchByTitle && /ムエット|mouette/i.test(name || '');
  const registry = new Map();
  // Liquid が出した判定（メタフィールド由来）を最優先し、無い場合だけ名前で判定する。
  const isSample = line => {
    if (line.sample === true) return true;
    if (line.sample === false) return false;
    const known = registry.get(String(line.variant_id || line.id));
    if (known && typeof known.sample === 'boolean') return known.sample;
    return sampleName(line.variant_title || line.variant?.title);
  };
  const totals = items => {
    const byProduct = new Map(); let total = 0;
    for (const line of items) if (isSample(line)) {
      const key = String(line.product_id), n = Number(line.quantity) || 0;
      byProduct.set(key, (byProduct.get(key) || 0) + n); total += n;
    }
    return {byProduct, total};
  };
  const problem = items => {
    const {byProduct, total} = totals(items);
    return [...byProduct.values()].some(n => n > perProductLimit) ? 'duplicate' : total > totalLimit ? 'limit' : '';
  };
  const additionProblem = (items, additions) => {
    const combined = items.map(x => ({...x}));
    for (const add of additions) {
      const info = registry.get(String(add.id));
      if (info?.sample) combined.push({...info, quantity:Number(add.quantity ?? 1)});
    }
    // 制限を入れる前から同じ香りが超えて入っているカートは、足す前に減らしてもらう（2026-10-25 本人）。
    // 入れようとした香りそのものの超過と合計の超過は、いままでどおりの文で止める。
    const a = totals(items), b = totals(combined);
    for (const [id, n] of b.byProduct) if (n > perProductLimit && n > (a.byProduct.get(id) || 0)) return 'duplicate';
    if ([...a.byProduct.values()].some(n => n > perProductLimit)) return 'reduce';
    return b.total > totalLimit && b.total > a.total ? 'limit' : '';
  };
  const permitsChange = (before, after) => {
    const a = totals(before), b = totals(after);
    // Let shoppers repair a pre-existing invalid cart one line at a time.
    if (b.total > totalLimit && b.total > a.total) return false;
    for (const [id, n] of b.byProduct) if (n > perProductLimit && n > (a.byProduct.get(id) || 0)) return false;
    return true;
  };
  const messages = {duplicate:'', limit:'', increase:'', retry:'', invalid:'', reduce:'', ...(config.messages || {})};
  let cart = null, pending = 0, queue = Promise.resolve(), scheduled = false;
  // 他のアプリがすでに fetch を包んでいる場合も、その関数をそのまま呼ぶ形で共存する。
  const nativeFetch = window.fetch.bind(window);
  const productScripts = new WeakSet(), cartScripts = new WeakSet(), originals = new WeakMap();
  const deniedForms = new WeakMap(), checkingForms = new WeakSet(), approvedForms = new WeakSet();
  // これまでに表示を書き換えたことのあるフォーム。元に戻す必要があるかの判断に使う。
  const touchedForms = new WeakSet();
  const cartChannel = typeof BroadcastChannel === 'function' ? new BroadcastChannel('lounge-mouette-cart') : null;
  let noticeId = 0;
  const announce = message => {
    let node = document.querySelector('#LoungeMouetteNotice');
    if (!node) {
      node = document.createElement('p'); node.id = 'LoungeMouetteNotice';
      node.className = 'visually-hidden'; node.setAttribute('role','status'); document.body.append(node);
    }
    node.textContent = message;
  };
  const refresh = async () => {
    // 言語つきの /en/cart.js などは選択肢の名前が訳されて返り、名前でのムエット判定が外れる。
    // 数えるためだけに読むので、いつも言語なしの /cart.js（日本語の名前）を読む（2026-10-26）。
    const response = await nativeFetch('/cart.js', {cache:'no-store',headers:{Accept:'application/json'}});
    if (!response.ok) throw new Error(messages.retry);
    const data = await response.json();
    if (!Array.isArray(data.items)) throw new Error(messages.retry);
    cart = data;
    data.items.forEach(line => {
      // Liquid が出した判定（メタフィールド由来）を cart.js の情報で上書きしない。
      const key = String(line.variant_id || line.id);
      const known = registry.get(key);
      const sample = known && typeof known.sample === 'boolean' ? known.sample : isSample(line);
      registry.set(key, {product_id: line.product_id, sample});
    });
    schedule(); return data;
  };
  const serialize = task => {
    const run = () => navigator.locks ? navigator.locks.request('lounge-mouette-cart',task) : task();
    const result = queue.then(run,run); queue = result.catch(() => {}); return result;
  };
  const readBody = async (input, init) => {
    let body = init?.body;
    if (body === undefined && input instanceof Request) body = await input.clone().text();
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const data = Object.fromEntries(body.entries());
      const items = [];
      for (const [key,value] of body.entries()) {
        const match = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
        if (match) { items[+match[1]] ||= {}; items[+match[1]][match[2]] = value; }
      }
      if (items.length) data.items = items.filter(Boolean);
      const updates = {};
      for (const [key,value] of body.entries()) {
        const match = key.match(/^updates\[([^\]]+)\]$/); if (match) updates[match[1]] = value;
      }
      if (Object.keys(updates).length) data.updates = updates;
      if (body.has('updates[]')) data.updates = body.getAll('updates[]');
      return data;
    }
    if (typeof body === 'string') { try { return JSON.parse(body); } catch { return readBody(null,{body:new URLSearchParams(body)}); } }
    return {};
  };
  const changedItems = (items, data, action) => {
    const result = items.map(line => ({...line}));
    if (action === 'change') {
      const line = data.line ? result[Number(data.line)-1] : result.find(x => x.key === String(data.id) || String(x.variant_id || x.id) === String(data.id));
      if (line && data.quantity !== undefined) line.quantity = Number(data.quantity);
    } else if (data.updates) {
      if (Array.isArray(data.updates)) result.forEach((line,i) => {if (data.updates[i] !== undefined) line.quantity = Number(data.updates[i]);});
      else for (const [key,quantity] of Object.entries(data.updates)) {
        const line = result.find(x => x.key === key || String(x.variant_id || x.id) === key);
        if (line) line.quantity = Number(quantity);
        else if (registry.has(key)) result.push({...registry.get(key),variant_id:key,quantity:Number(quantity)});
      }
    }
    return result;
  };
  window.LoungeMouetteLimits = {config, perProductLimit, totalLimit, sampleName, totals, problem, additionProblem, permitsChange, changedItems, registry};
  const wrappedFetch = function(input, init) {
    // 想定外のリクエスト形式でカート操作そのものを止めないよう、判定は必ず try で囲む。
    let action, method;
    try {
      const url = new URL(input instanceof Request ? input.url : input, location.href);
      action = url.origin === location.origin && url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?$/)?.[1];
      method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    } catch { return nativeFetch(input,init); }
    if (!action || method !== 'POST') return nativeFetch(input,init);
    return serialize(async () => {
      pending++; schedule();
      try {
        ingest();
        const data = await readBody(input,init), before = await refresh();
        let error = '';
        if (action === 'add') {
          const additions = data.items || [{id:data.id,quantity:data.quantity}];
          // Native form metadata identifies samples without relying on price or customer properties.
          if (additions.some(item => registry.get(String(item.id))?.sample)) error = additionProblem(before.items,additions);
        } else if (action !== 'clear' && !permitsChange(before.items,changedItems(before.items,data,action))) error = 'invalid';
        if (error) {
          announce(messages[error]);
          if (action === 'add') return new Response(JSON.stringify({status:422,message:messages[error],description:messages[error]}),{status:422,headers:{'Content-Type':'application/json'}});
          throw new Error(messages[error]);
        }
        const response = await nativeFetch(input,init);
        await refresh().catch(() => {cart = null;});
        if(response.ok) cartChannel?.postMessage('changed');
        return response;
      } finally {pending--; schedule();}
    });
  };
  wrappedFetch.loungeMouette = true;
  window.fetch = wrappedFetch;
  const ingest = () => {
    document.querySelectorAll('[data-mouette-product]').forEach(node => {
      if (productScripts.has(node)) return; productScripts.add(node);
      try {
        const data = JSON.parse(node.textContent);
        data.variants.forEach(v => registry.set(String(v.id),{product_id:data.product_id,sample:v.sample}));
      } catch { /* A malformed unrelated form must not interrupt the page. */ }
    });
    document.querySelectorAll('[data-mouette-cart]').forEach(node => {
      if (cartScripts.has(node)) return; cartScripts.add(node);
      // Only use Liquid as the initial snapshot. Requests provide newer state.
      if (!cart) try {cart = JSON.parse(node.textContent);} catch {}
    });
  };
  const remember = (node, names) => {
    if (!originals.has(node)) originals.set(node,Object.fromEntries(names.map(name=>[name,node.getAttribute(name)])));
  };
  const set = (node,name,value) => {
    if (value === null) {if(node.hasAttribute(name)) node.removeAttribute(name);}
    else if (node.getAttribute(name) !== String(value)) node.setAttribute(name,String(value));
  };
  const restore = node => {
    const saved = originals.get(node); if (!saved) return;
    for (const [name,value] of Object.entries(saved)) set(node,name,value);
    originals.delete(node);
  };
  const syncQuantity = (component, limited, current = 1) => {
    const input = component.querySelector('[data-quantity-input]'); if (!input) return;
    const plus = component.querySelector('quantity-btn[data-action="plus"]');
    // 1 種類あたりの上限を設けていない場合は、数量欄に手を加えない。
    if (limited && perProductLimit === Infinity) limited = false;
    if (!limited) {
      if (originals.has(input)) {restore(input); restore(component); if(plus){restore(plus);plus.querySelectorAll('button').forEach(restore);} component.updateButtonsDisable?.();}
      if (plus) set(plus,'data-mouette-plus',null);
      component.querySelector('[data-mouette-quantity-note]')?.remove();
      return;
    }
    // すでに上限を超えている行は減らせるように、現在の数量までは残す。
    const allowed = Math.max(perProductLimit, current);
    remember(input,['max','readonly']); remember(component,['data-stock']);
    set(input,'max',allowed); set(input,'readonly',''); set(component,'data-stock',allowed);
    if (!component.closest('cart-item') && Number(input.value) > perProductLimit) input.value = String(perProductLimit);
    // 上限にまだ達していなければ「＋」は使えるままにする。
    if (plus && current < perProductLimit) {
      restore(plus); plus.querySelectorAll('button').forEach(restore);
      set(plus,'data-mouette-plus',null);
      component.querySelector('[data-mouette-quantity-note]')?.remove();
      component.updateButtonsDisable?.();
    } else if (plus) {
      let note = component.querySelector('[data-mouette-quantity-note]');
      if (!note) {
        note = document.createElement('span'); note.id = 'MouetteQuantityNote-' + (++noticeId);
        note.setAttribute('data-mouette-quantity-note',''); note.setAttribute('role','tooltip');
        note.textContent = messages.duplicate; component.append(note);
      }
      // Keep a focusable/tappable control; capture handlers prevent quantity changes.
      remember(plus,['disabled','aria-disabled','tabindex']); set(plus,'disabled',null); set(plus,'aria-disabled','true');set(plus,'tabindex','-1');
      set(plus,'data-mouette-plus','');
      plus.querySelectorAll('button').forEach(button => {
        remember(button,['disabled','aria-disabled','aria-describedby','aria-label']);
        set(button,'disabled',null); set(button,'aria-disabled','true'); set(button,'aria-describedby',note.id);set(button,'aria-label',messages.increase);
      });
    }
  };
  const sync = () => {
    ingest(); const invalid = cart && problem(cart.items);
    document.querySelectorAll('product-form').forEach(component => {
      const form = component.querySelector('form'), input = form?.querySelector('[name="id"]'); if(!input) return;
      const info = registry.get(String(input.value)), limited = Boolean(info?.sample);
      const denial = deniedForms.get(form);
      if (denial && (!limited || denial.variant !== String(input.value) ||
          (cart && !additionProblem(cart.items,[{id:input.value,quantity:1}])))) deniedForms.delete(form);
      const reason = deniedForms.has(form) ? (cart ? additionProblem(cart.items,[{id:input.value,quantity:1}]) : denial.reason) : '';
      const label = reason ? messages[reason] : '';
      /* ムエットでもなく、警告も出しておらず、これまで一度も手を加えていないフォームは
         この先の処理がすべて「何も書き換えない」で終わる。走査だけが残るので飛ばす。
         コレクションページのように商品カードが並ぶ場面で効く。
         一度でも手を加えたフォームは touchedForms に入れてあり、
         ムエットでなくなった回に元へ戻す処理を通してから外す。 */
      const busy = checkingForms.has(form);
      if (!limited && !label && !busy && !touchedForms.has(form)) return;
      if (limited || label || busy) touchedForms.add(form); else touchedForms.delete(form);
      component.querySelectorAll('button[type="submit"]').forEach(button => {
        if(label) {remember(button,['aria-disabled','aria-label']);set(button,'data-mouette-label',label);set(button,'aria-disabled','true');set(button,'aria-label',label);}
        else {set(button,'data-mouette-label',null);restore(button);}
        set(button,'aria-busy',busy?'true':null);
        const text = button.querySelector('[data-button-text]');
        if (text) {
          if(label) {if(!text.dataset.mouetteOriginal) text.dataset.mouetteOriginal=text.innerHTML;if(text.textContent!==label) text.textContent=label;}
          else if(text.dataset.mouetteOriginal){text.innerHTML=text.dataset.mouetteOriginal;delete text.dataset.mouetteOriginal;}
        }
      });
      const productInfo = component.closest('product-info');
      productInfo?.querySelectorAll('[data-mouette-only]').forEach(node=>set(node,'hidden',limited?null:''));
      const quantities = new Set([...component.querySelectorAll('quantity-component'),...Array.from(productInfo?.querySelectorAll('quantity-component')||[]).filter(q=>q.querySelector('input')?.form===form)]);
      quantities.forEach(q=>syncQuantity(q,limited));
      component.querySelectorAll('[data-lounge-preorder-checkout], .shopify-payment-button').forEach(node=>set(node,'data-mouette-hide-express',limited?'':null));
    });
    document.querySelectorAll('cart-item').forEach(item => {
      const line = (cart?.items||[]).find(x=>String(x.variant_id||x.id)===item.dataset.variantId);
      item.querySelectorAll('quantity-component').forEach(q=>syncQuantity(q,Boolean(line&&isSample(line)),Number(item.dataset.quantity)||1));
    });
    document.querySelectorAll('lounge-mouette-consent').forEach(guard=>{
      guard.hidden=!invalid; const warning=guard.querySelector('[data-mouette-warning]');
      if(warning && warning.textContent!==(invalid?messages.invalid:'')) warning.textContent=invalid?messages.invalid:'';
    });
    // カートページの「関連商品」から足したときに出るポップアップの「チェックアウト」も止める（2026-10-26）。
    document.querySelectorAll('cart-page,cart-drawer,cart-notification-popup').forEach(root=>{
      const locked=Boolean(invalid||pending);
      root.querySelectorAll('button[name="checkout"]').forEach(button=>{
        if(locked){remember(button,['aria-disabled']);set(button,'data-lounge-mouette-lock','true');set(button,'aria-disabled','true');}
        else {set(button,'data-lounge-mouette-lock',null);restore(button);}
      });
      root.querySelectorAll('.dynamic-checkout-buttons').forEach(node=>set(node,'data-lounge-mouette-lock',locked?'true':null));
    });
  };
  /* 点検をまとめるタイミング。
     ブラウザが画面を描き直す直前に 1 回だけ動かす。
     Aurora がカートや商品情報を差し替えるときの連続した変化も、
     この点検自身の書き換えによる再点検も、まとめて 1 回になる。
     画面が描かれる前に必ず終わるので、お客様に途中の状態は見えない。
     タブが裏に回っていると描き直しが起きず点検が止まるので、そのときだけタイマーで動かす。 */
  function schedule(){
    if(scheduled)return;
    scheduled=true;
    const run=()=>{scheduled=false;sync();};
    if(document.hidden) setTimeout(run,0); else requestAnimationFrame(run);
  }
  const quantityNote = plus => plus.closest('quantity-component')?.querySelector('[data-mouette-quantity-note]');
  const showQuantityNote = plus => {const note=quantityNote(plus);if(note)set(note,'data-open','');};
  const stop = event => {event.preventDefault();event.stopImmediatePropagation();};
  document.addEventListener('click',event=>{
    const plus=event.target.closest('[data-mouette-plus]');
    if(plus){stop(event);showQuantityNote(plus);announce(messages.duplicate);return;}
    document.querySelectorAll('[data-mouette-quantity-note][data-open]').forEach(note=>set(note,'data-open',null));
    const button=event.target.closest('button[data-mouette-label],button[data-lounge-mouette-lock]');
    if(button){event.preventDefault();event.stopImmediatePropagation();announce(button.dataset.mouetteLabel||messages.invalid);refresh().catch(()=>{});}
  },true);
  document.addEventListener('pointerover',event=>{const plus=event.target.closest('[data-mouette-plus]');if(plus)showQuantityNote(plus);},true);
  /* 数量欄から出たら閉じる。
     **「＋」を押すとそのボタンに焦点が残る**ので、以前のように
     plus.contains(document.activeElement) を条件に入れていると、
     一度でも押したあとはマウスが離れても出たままになった。
     キーボードの人は下の focusout が面倒を見るので、ここは位置だけで決める。
     見張る先も「＋」ではなく数量欄ぜんたい——吹き出しは欄の側に置いてあり、
     そこへマウスが移っただけで消えるのは、読む前に消えるということ。 */
  document.addEventListener('pointerout',event=>{
    const host=event.target.closest('quantity-component');
    if(!host)return;
    const note=host.querySelector('[data-mouette-quantity-note][data-open]');
    if(!note||host.contains(event.relatedTarget))return;
    set(note,'data-open',null);
  },true);
  document.addEventListener('focusin',event=>{const plus=event.target.closest('[data-mouette-plus]');if(plus)showQuantityNote(plus);},true);
  document.addEventListener('focusout',event=>{const plus=event.target.closest('[data-mouette-plus]');if(plus){const note=quantityNote(plus);if(note)set(note,'data-open',null);}},true);
  document.addEventListener('keydown',event=>{
    if(event.key==='Escape') document.querySelectorAll('[data-mouette-quantity-note][data-open]').forEach(note=>set(note,'data-open',null));
    const plus=event.target.closest('[data-mouette-plus]');
    if(plus&&(event.key==='Enter'||event.key===' ')){stop(event);showQuantityNote(plus);announce(messages.duplicate);}
  },true);
  const checkSubmission = async (form, component, submitter, variant) => {
    checkingForms.add(form); sync();
    try {
      const quantity = Number(new FormData(form).get('quantity') || 1);
      const error = await serialize(async()=>additionProblem((await refresh()).items,[{id:variant,quantity}]));
      if(!form.isConnected || String(form.querySelector('[name="id"]')?.value)!==variant) return;
      if(error){deniedForms.set(form,{variant,reason:error});announce(messages[error]);return;}
      // Release the cart lock before native submission takes its own write lock.
      approvedForms.add(form);
      try {if(submitter?.isConnected)form.requestSubmit(submitter);else form.requestSubmit();}
      finally {approvedForms.delete(form);}
    } catch {
      let note=component.querySelector('[data-mouette-form-error]');
      if(!note){note=document.createElement('p');note.setAttribute('data-mouette-form-error','');note.setAttribute('role','alert');form.append(note);}
      note.textContent=messages.retry; announce(messages.retry);
    } finally {checkingForms.delete(form);sync();}
  };
  document.addEventListener('submit',event=>{
    const component=event.target.closest('product-form');
    if(component){
      const form=event.target;
      if(approvedForms.has(form))return;
      sync();
      if(checkingForms.has(form)||component.querySelector('button[data-mouette-label]')){stop(event);return;}
      const variant=String(form.querySelector('[name="id"]')?.value);
      if(registry.get(variant)?.sample){
        stop(event);component.querySelector('[data-mouette-form-error]')?.remove();
        checkSubmission(form,component,event.submitter,variant);
      }
    }
    else if(event.target.id==='CartPageForm'||event.target.id==='CartDrawerForm') {
      if(pending||!cart||problem(cart.items)){event.preventDefault();event.stopImmediatePropagation();announce(messages.invalid);}
    }
  },true);
  document.addEventListener('change',event=>{
    const input=event.target;
    if(input.matches('[data-quantity-input]')&&input.readOnly) {
      const item=input.closest('cart-item'), current=Number(item?.dataset.quantity)||1;
      if(Number(input.value)>perProductLimit&&Number(input.value)>=current){input.value=String(current);event.stopImmediatePropagation();event.preventDefault();announce(messages.duplicate);}
    }
    schedule();
  },true);
  const observer=new MutationObserver(schedule);
  observer.observe(document.documentElement,{childList:true,subtree:true,attributes:true,attributeFilter:['value','disabled']});
  window.addEventListener('pageshow',()=>refresh().catch(()=>{}));
  if(cartChannel) cartChannel.onmessage=()=>refresh().catch(()=>{});
  window.addEventListener('focus',()=>refresh().catch(()=>{}));
  document.addEventListener('visibilitychange',()=>{if(!document.hidden)refresh().catch(()=>{});});
  sync(); refresh().catch(()=>{});
})();
